import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = ({ enabled = true }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Verificar se já está instalado
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Verificar se é iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Verificar se já foi fechado recentemente
    const dismissed = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < dayInMs) {
        return; // Não mostrar por 24h
      }
    }

    // Capturar o evento beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (enabled && !standalone) {
        setTimeout(() => setShowPrompt(true), 3000); // Mostrar após 3s
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Se for iOS e não estiver em standalone, mostrar prompt customizado
    if (ios && !standalone && enabled) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [enabled]);

  // Registrar Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registrado:', registration.scope);
        })
        .catch((error) => {
          console.log('SW erro:', error);
        });
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  if (!enabled || isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] animate-slide-up">
      <div className="bg-gradient-to-r from-purple-500 to-violet-600 rounded-2xl p-4 shadow-2xl border border-purple-400/30">
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -right-2 w-7 h-7 bg-black/80 rounded-full flex items-center justify-center text-white hover:bg-black transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
            <img 
              src="/icon-96x96.png" 
              alt="Fox Seller" 
              className="w-10 h-10 rounded-lg"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-base">
              Instalar Fox Seller
            </h3>
            <p className="text-purple-100 text-sm mt-0.5">
              Adicione à tela inicial para acesso rápido!
            </p>
          </div>
        </div>

        {isIOS ? (
          <div className="mt-3 bg-black/20 rounded-xl p-3">
            <p className="text-white text-sm text-center">
              Toque em <span className="inline-flex items-center bg-white/20 px-2 py-1 rounded mx-1">
                <img src="https://cdn-icons-png.flaticon.com/512/157/157960.png" alt="compartilhar" className="w-4 h-4 invert" />
              </span> e depois em <strong>"Adicionar à Tela de Início"</strong>
            </p>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            className="mt-3 w-full bg-white text-purple-600 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-purple-50 transition-colors shadow-lg"
          >
            <Download className="w-5 h-5" />
            Instalar Agora
          </button>
        )}
      </div>
      
      {/* Seta fora do card - só para iOS */}
      {isIOS && (
        <div className="flex justify-center mt-2">
          <div className="animate-bounce bg-white rounded-full p-2 shadow-lg">
            <svg 
              width="36" 
              height="36" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M12 4L12 20M12 20L6 14M12 20L18 14" 
                stroke="#7c3aed" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAInstallPrompt;
